'use client';

import { c as _c } from "react/compiler-runtime";
import { motion } from 'framer-motion';
import Image from 'next/image';
import { Calendar, Cpu, Globe, Lightbulb, Rocket, Target, Zap } from 'lucide-react';
import FadeContent from '@/components/ui/fade-content';
import Hyperspeed from '@/components/ui/Hyperspeed';
import GradientText from '@/components/ui/GradientText';
import ShinyText from '@/components/ui/ShinyText';
import { hyperspeedPresets } from '@/components/ui/hyperspeed-presets';
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};
export default function AboutPage() {
  const $ = _c(28);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="absolute inset-0 z-0"><Hyperspeed effectOptions={hyperspeedPresets.six as any} /></div>;
    t1 = <div className="absolute inset-0 bg-background/30 backdrop-blur-[1px] z-1" />;
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span className="inline-block py-1 px-3 rounded-full bg-primary/10 text-primary text-sm font-medium tracking-wide">THE VISION</span>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = ["#40ffaa", "#4079ff", "#40ffaa", "#4079ff", "#40ffaa"];
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <GradientText colors={t3} animationSpeed={3} showBorder={false} className="text-6xl md:text-8xl font-bold tracking-tight text-center leading-tight"><h1>Innovating at the <br />{"Frontier of AI & Web"}</h1></GradientText>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <section className="relative min-h-screen flex items-center justify-center pt-10 pb-12 overflow-hidden">{t0}{t1}<div className="container relative z-10 text-center"><motion.div initial="hidden" animate="visible" variants={fadeInUp} className="space-y-6 max-w-4xl mx-auto">{t2}<div className="flex justify-center relative">{t4}<div className="absolute inset-0 pointer-events-none z-10 flex justify-center mix-blend-overlay"><ShinyText disabled={false} speed={3} className="text-6xl md:text-8xl font-bold tracking-tight text-center leading-tight" color="transparent" shineColor="white"><h1>Innovating at the <br />{"Frontier of AI & Web"}</h1></ShinyText></div></div><p className="text-xl md:text-2xl text-muted-foreground leading-relaxed max-w-2xl mx-auto">Bridging the gap between human creativity and machine intelligence to build the digital future.</p></motion.div></div></section>;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      once: true
    };
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <motion.div variants={fadeInUp} className="space-y-4"><div className="flex items-center gap-3 text-2xl font-bold"><Target className="w-8 h-8 text-primary" /><h2>Our Mission</h2></div><p className="text-lg text-muted-foreground leading-relaxed">To democratize access to advanced technology knowledge. We believe that understanding AI and modern web architecture shouldn't be reserved for the elite few, but accessible to every ambitious developer.</p></motion.div>;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <motion.div initial="hidden" whileInView="visible" viewport={t6} variants={staggerContainer} className="space-y-8">{t7}<motion.div variants={fadeInUp} className="space-y-4"><div className="flex items-center gap-3 text-2xl font-bold"><Lightbulb className="w-8 h-8 text-accent-orange" /><h2>The Motto</h2></div><blockquote className="text-xl font-medium italic border-l-4 border-primary pl-6 py-2">"Code is not just syntax; it's the language of possibilities. We write to empower, to innovate, and to inspire."</blockquote></motion.div></motion.div>;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t10;
  let t11;
  let t12;
  let t9;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      opacity: 0,
      scale: 0.8
    };
    t10 = {
      opacity: 1,
      scale: 1
    };
    t11 = {
      once: true
    };
    t12 = {
      duration: 0.8
    };
    $[9] = t10;
    $[10] = t11;
    $[11] = t12;
    $[12] = t9;
  } else {
    t10 = $[9];
    t11 = $[10];
    t12 = $[11];
    t9 = $[12];
  }
  let t13;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <div className="absolute inset-0 flex items-center justify-center"><Globe className="w-64 h-64 text-primary/5 animate-spin-slow" /><Cpu className="absolute w-32 h-32 text-primary/20" /></div>;
    $[13] = t13;
  } else {
    t13 = $[13];
  }
  let t14;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <section className="py-20 bg-muted/20"><div className="container"><FadeContent blur={true} duration={1} threshold={0.2}><div className="grid md:grid-cols-2 gap-16 items-center">{t8}<motion.div initial={t9} whileInView={t10} viewport={t11} transition={t12} className="relative h-[400px] rounded-3xl overflow-hidden shadow-2xl bg-gradient-to-br from-card to-muted border border-border">{t13}<div className="absolute bottom-0 left-0 right-0 p-8 bg-gradient-to-t from-background to-transparent"><div className="text-3xl font-bold text-center">Tech-Knowlogia</div><div className="text-center text-muted-foreground">Est. 2025</div></div></motion.div></div></FadeContent></div></section>;
    $[14] = t14;
  } else {
    t14 = $[14];
  }
  let t15;
  let t16;
  let t17;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      opacity: 0,
      y: 20
    };
    t16 = {
      opacity: 1,
      y: 0
    };
    t17 = {
      once: true
    };
    $[15] = t15;
    $[16] = t16;
    $[17] = t17;
  } else {
    t15 = $[15];
    t16 = $[16];
    t17 = $[17];
  }
  let t18;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <h2 className="text-3xl md:text-4xl font-bold mb-4">Content Release Schedule</h2>;
    $[18] = t18;
  } else {
    t18 = $[18];
  }
  let t19;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <motion.div initial={t15} whileInView={t16} viewport={t17} className="text-center mb-16">{t18}<p className="text-lg text-muted-foreground">Delivering <span className="text-primary font-bold">10 Premium Articles</span> every single week.</p></motion.div>;
    $[19] = t19;
  } else {
    t19 = $[19];
  }
  let t20;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <section className="py-24"><div className="container max-w-4xl mx-auto">{t19}<div className="relative border-l-2 border-primary/20 ml-4 md:ml-0 md:pl-0 space-y-12">{SCHEDULE.map(_temp2)}</div></div></section>;
    $[20] = t20;
  } else {
    t20 = $[20];
  }
  let t21;
  let t22;
  let t23;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = {
      opacity: 0,
      scale: 0.9
    };
    t22 = {
      opacity: 1,
      scale: 1
    };
    t23 = {
      once: true
    };
    $[21] = t21;
    $[22] = t22;
    $[23] = t23;
  } else {
    t21 = $[21];
    t22 = $[22];
    t23 = $[23];
  }
  let t24;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <motion.div initial={t21} whileInView={t22} viewport={t23} className="relative w-48 h-48 md:w-64 md:h-64 flex-shrink-0"><div className="absolute inset-0 bg-gradient-to-br from-primary to-accent-purple rounded-full blur-2xl opacity-50" /><Image src="/images/profile.jpg" alt="Lalit Choudhary" fill={true} className="object-cover rounded-full border-4 border-background relative z-10 shadow-xl" /></motion.div>;
    $[24] = t24;
  } else {
    t24 = $[24];
  }
  let t25;
  let t26;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <div><h2 className="text-3xl font-bold">Lalit Choudhary</h2><p className="text-lg text-primary font-medium mt-1">{"Full Stack Innovator & Tech Writer"}</p></div>;
    t26 = <p className="text-lg text-muted-foreground leading-relaxed">"I build systems that scale and write words that inspire. My journey is defined by a relentless pursuit of the innovative edge in Full Stack Development and Artificial Intelligence."</p>;
    $[25] = t25;
    $[26] = t26;
  } else {
    t25 = $[25];
    t26 = $[26];
  }
  let t27;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <div className="bg-background overflow-hidden">{t5}{t14}{t20}<section className="py-20 bg-primary/5"><div className="container max-w-4xl"><div className="flex flex-col md:flex-row gap-12 items-center">{t24}<div className="space-y-6 text-center md:text-left">{t25}{t26}<div className="flex gap-4 justify-center md:justify-start">{["Next.js 16", "React 19", "MongoDB", "AI Agents", "System Design"].map(_temp3)}</div></div></div></div></section></div>;
    $[27] = t27;
  } else {
    t27 = $[27];
  }
  return t27;
}
function _temp3(tech) {
  return <span key={tech} className="px-3 py-1 bg-background border border-border rounded-full text-xs font-semibold shadow-sm">{tech}</span>;
}
function _temp2(item, index) {
  return <motion.div key={item.day} initial={{
    opacity: 0,
    x: -20
  }} whileInView={{
    opacity: 1,
    x: 0
  }} viewport={{
    once: true
  }} transition={{
    delay: index * 0.1
  }} className="relative md:grid md:grid-cols-5 md:gap-8 items-center"><div className="absolute -left-[9px] top-1 md:relative md:left-auto md:top-auto md:col-span-1 md:flex md:justify-end"><div className={`w-4 h-4 rounded-full border-2 border-background ${item.color} shadow-[0_0_0_4px_rgba(var(--primary),0.2)]`} /></div><div className="pl-8 md:pl-0 md:col-span-4"><div className="bg-card border border-border rounded-xl p-6 hover:shadow-lg transition-shadow duration-300"><div className="flex flex-wrap justify-between items-start mb-2"><h3 className="text-xl font-bold">{item.day}</h3><span className="text-xs font-semibold px-2 py-1 rounded bg-secondary text-secondary-foreground">2 New Articles</span></div><div className="flex gap-4 mt-4">{item.topics.map(_temp)}</div></div></div></motion.div>;
}
function _temp(topic) {
  return <div key={topic.name} className="flex items-center gap-2 text-sm text-muted-foreground"><topic.icon className="w-4 h-4 text-primary" /><span>{topic.name}</span></div>;
}
const SCHEDULE = [{
  day: 'Monday',
  color: 'bg-blue-500',
  topics: [{
    name: 'Web Development',
    icon: Globe
  }, {
    name: 'System Architecture',
    icon: ServerIcon
  }]
}, {
  day: 'Tuesday',
  color: 'bg-emerald-500',
  topics: [{
    name: 'Artificial Intelligence',
    icon: Cpu
  }, {
    name: 'Machine Learning',
    icon: Zap
  }]
}, {
  day: 'Wednesday',
  color: 'bg-purple-500',
  topics: [{
    name: 'UI/UX Design',
    icon: Rocket
  }, {
    name: 'Frontend Trends',
    icon: SparklesIcon
  }]
}, {
  day: 'Thursday',
  color: 'bg-orange-500',
  topics: [{
    name: 'Tech Business',
    icon: Target
  }, {
    name: 'Startup Strategy',
    icon: BriefcaseIcon
  }]
}, {
  day: 'Friday',
  color: 'bg-pink-500',
  topics: [{
    name: 'Lifestyle & Productivity',
    icon: CoffeeIcon
  }, {
    name: 'Developer Health',
    icon: HeartIcon
  }]
}];

// Mock Icons for timeline data
function ServerIcon(props) {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <Cpu {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
function SparklesIcon(props) {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <Rocket {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
function BriefcaseIcon(props) {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <Target {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
function CoffeeIcon(props) {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <Zap {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
function HeartIcon(props) {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <Target {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}